/**
 * Numbers the tab labels that would still read the same — two lines of one product share their
 * product and flow names — so the client can tell which tab is which line. Unique labels are untouched.
 *
 * @example
 * numberRepeatedLabels(['2D floor plan', '2D floor plan', 'Staging']) // ['2D floor plan (1)', '2D floor plan (2)', 'Staging']
 */
export const numberRepeatedLabels = (labels: readonly string[]): string[] => {
  const totals = labels.reduce<Record<string, number>>((acc, label) => ({ ...acc, [label]: (acc[label] ?? 0) + 1 }), {})
  const seen: Record<string, number> = {}
  return labels.map((label) => {
    if (totals[label] === 1) return label
    seen[label] = (seen[label] ?? 0) + 1
    return `${label} (${seen[label]})`
  })
}

interface LabelledTab {
  cartItemId: string
  flowId: string
  flowCountOnItem: number
}

/**
 * The chip label of every configuration tab: the product's name, qualified with the flow when the name alone
 * is not unique (two lines of one group, or one line with several flows), then numbered where still repeated.
 * Shared by the chip strip and the Proceed hint, so the hint names the tabs exactly as the chips do.
 */
export const configurationTabLabels = (
  tabs: readonly LabelledTab[],
  productName: (cartItemId: string) => string,
  flowLabel: (flowId: string) => string
): string[] => {
  const counts = tabs.reduce<Record<string, number>>((acc, tab) => {
    const base = productName(tab.cartItemId)
    return { ...acc, [base]: (acc[base] ?? 0) + 1 }
  }, {})
  return numberRepeatedLabels(tabs.map((tab) => {
    const base = productName(tab.cartItemId)
    const needsFlow = tab.flowCountOnItem > 1 || counts[base] > 1
    return needsFlow ? `${base} - ${flowLabel(tab.flowId)}` : base
  }))
}
